import type { Branch } from "@domain/CRM/branch";
import type { Company } from "@domain/CRM/company";
import type { Contact } from "@domain/CRM/contact";
import type { Deal } from "@domain/CRM/deal";
import type { Grower } from "@domain/CRM/grower";
import type { Region } from "@domain/CRM/region";
import type { User } from "@domain/Auth";
import type { Job } from "@domain/CRM/job";
import type { JobFlags } from "@domain/CRM/job";
import { finalizeJobForSubmit, applyReadyAtSubmission } from "@domain/CRM/job";
import { err, ok, type Result } from "@domain/Result";
import type { LogData } from "@rogoag/shared-ts";
import type { IJobCreation } from "@ports/jobs/IJobCreation";
import type { ILogger } from "@ports/logger/ILogger";
import {
	type JobCreationContext,
	associateExternalGrowers,
	addNewSalespersons,
	associateSalespersonsAndBranches,
	prepareJobsForCreation,
} from "./submitJobsUseCases.helpers";

export interface SubmitJobsUseCasesDeps {
	jobCreationApi: IJobCreation;
	getCurrentUser(): Promise<User>;
	getDealsByIds(ids: string[]): Promise<Deal[]>;
	createJobs(jobs: Partial<Job>[]): Promise<Job[]>;
	logger: ILogger;
	/** Read-side ports for company-scoped initial-load + company-context refresh. The
	 *  optional `fresh` flag on each bypasses the server-side read cache while still
	 *  repopulating it — see `useFreshOnEntry`. */
	getBranchesByCompany?(company: Company, fresh?: boolean): Promise<Branch[]>;
	getRegionsByCompany?(company: Company, fresh?: boolean): Promise<Region[]>;
	getGrowersByCompany?(company: Company, fresh?: boolean): Promise<Grower[]>;
	getDealsByCompany?(company: Company, fresh?: boolean): Promise<Deal[]>;
	getContactsByCompany?(company: Company, fresh?: boolean): Promise<Contact[]>;
	getLastCropOptions?(fresh?: boolean): Promise<string[]>;
	getNextCropOptions?(fresh?: boolean): Promise<string[]>;
	/** Updates a grower's phone number in place. Used by `submitSingleJob`
	 *  to keep the grower record in sync when the user changed the phone in
	 *  the form for an *existing* grower. */
	updateGrowerPhoneNumber?(growerId: string, phoneNumber: string): Promise<void>;
}

export interface CompanyContextResult {
	branches: Branch[];
	regions: Region[];
	growers: Grower[];
	deals: Deal[];
	/** Raw contacts for the company; the caller applies `sortContacts` (the
	 *  contact sort lives at the GUI seam because the helper barrel re-exports
	 *  it for legacy mock paths). */
	contacts: Contact[];
}

export interface InitialDataInput<O> {
	user: User | undefined;
	company: Company | null;
	branches: Branch[];
	/** Build the option list. Generic over `O` so the use case stays decoupled
	 *  from `@domain/Settings` — the GUI passes `ImportOption[]`,
	 *  CLI/tests pass anything matching the contract. */
	buildImportOptions(user: User, company: Company | null, branches: Branch[]): Promise<O[]>;
	/** Bypasses the server-side read cache for the crop-option reads while still
	 *  repopulating it — see `useFreshOnEntry`. */
	fresh?: boolean;
}

export interface InitialDataResult<O> {
	importOptions: O[];
	lastCrops: string[];
	nextCrops: string[];
}

export type LoadInitialDataError = "missing_user";

/**
 * Job-submission use cases. `submitJobs` orchestrates the full creation
 * pipeline (associate growers, add salespersons, link branches, prepare
 * payload, create) by composing the helpers in `./submitJobsUseCases.helpers`
 * with the injected `IJobCreation` port and a few additional dependencies.
 *
 * Viewmodels and components should use this instead of calling
 * `API.Data.Jobs.create.many` directly — the API layer is transport-only and
 * does not perform orchestration.
 */
export interface SubmitJobsUseCases {
	submitJobs(jobs: Partial<Job>[], source?: JobFlags): Promise<Job[]>;
	submitJob(job: Partial<Job>, source?: JobFlags): Promise<Job>;
	submitSingleJob(job: Partial<Job>, source?: JobFlags): Promise<Job>;
	/** The optional `input.fresh` flag bypasses the server-side read cache for the
	 *  crop-option reads while still repopulating it — see `useFreshOnEntry`. */
	loadInitialData<O>(input: InitialDataInput<O>): Promise<Result<InitialDataResult<O>, LoadInitialDataError>>;
	/** Same `fresh` semantics as `loadInitialData`, applied to the branches/regions/growers/
	 *  deals/contacts reads. */
	loadCompanyContext(company: Company, fresh?: boolean): Promise<CompanyContextResult>;
}

export async function submitJobs(
	deps: SubmitJobsUseCasesDeps,
	jobs: Partial<Job>[],
	source?: JobFlags,
): Promise<Job[]> {
	const user = await deps.getCurrentUser();
	const uniqueDealIds = Array.from(new Set(jobs.filter((job) => job.dealIds?.[0]).map((job) => job.dealIds![0])));
	const deals = uniqueDealIds.length > 0 ? await deps.getDealsByIds(uniqueDealIds) : [];

	const logData: LogData = {
		function: "submitJobs",
		jobs_count: jobs.length,
		source,
		user: user.email,
	};

	const ctx: JobCreationContext = {
		userEmail: user.email,
		userCompanyIds: user.companyIds,
		isAdmin: user.isAdmin,
		source,
		deals,
		logger: deps.logger,
	};

	// Growers and salespersons are independent — parallelize.
	// Branch association depends on salesperson IDs, so it runs after.
	await Promise.all([
		associateExternalGrowers(jobs, ctx, deps.jobCreationApi, { ...logData }),
		addNewSalespersons(jobs, ctx, deps.jobCreationApi, { ...logData }),
	]);
	await associateSalespersonsAndBranches(jobs, deps.jobCreationApi, logData, deps.logger);
	// Strip the four UI-only sentinels ("custom" deal, "Create new grower..." /
	// CREATE_NEW_SALESPERSON links, "New Grower: " name prefix) as the final step
	// before create. Airtable link fields reject the sentinel strings with a 422
	// (INVALID_RECORD_ID) — the previous home for this call was the standalone
	// SingleImportForm, deleted in the wizard migration, so it must live here in
	// the shared pipeline where every submit path (geo-files/Agrian/Proagrica) hits it.
	// One marking timestamp for the batch: a job marked "ready to sample" at
	// import is stamped Marked Ready as of submission (replaces the retired
	// "Field Ready at Submission?" field).
	const markedReadyAt = new Date();
	const jobsToCreate = prepareJobsForCreation(jobs, ctx, logData)
		.map(finalizeJobForSubmit)
		.map((job) => applyReadyAtSubmission(job, markedReadyAt));

	logData.function = "submitJobs.creatingJobs";
	deps.logger.log("Creating jobs", logData);
	return await deps.createJobs(jobsToCreate);
}

/** Convenience wrapper for the single-job case. */
export async function submitJob(deps: SubmitJobsUseCasesDeps, job: Partial<Job>, source?: JobFlags): Promise<Job> {
	const [created] = await submitJobs(deps, [job], source);
	return created;
}

/**
 * Single-job submit with the grower-phone-sync side effect folded in.
 * The single-import form lets the user edit the grower's phone *while*
 * picking that grower; if the grower already exists in Airtable (i.e.
 * `growerIds[0]` is a real id, not the `""` sentinel for "create new")
 * and a phone is present in the form, we POST that update before the
 * job submission.
 *
 * Phone-sync errors propagate (the grower record matters more than the
 * job itself; the calling vm catches and toasts).
 */
export async function submitSingleJob(
	deps: SubmitJobsUseCasesDeps,
	job: Partial<Job>,
	source?: JobFlags,
): Promise<Job> {
	const firstGrowerId = job.growerIds?.[0];
	if (firstGrowerId && firstGrowerId !== "" && job.growerPhoneNumber) {
		await deps.updateGrowerPhoneNumber?.(firstGrowerId, job.growerPhoneNumber);
	}
	return submitJob(deps, job, source);
}

/**
 * Fetches the non-company-specific data the SubmitJobs page needs at
 * mount: import-option list (built from user role + company config flags),
 * plus the lastCrop / nextCrop autocomplete option lists.
 *
 * Returns `Result.err("missing_user")` when no user is supplied or the
 * user lacks an id — the page shows an error toast and bails out.
 *
 * `buildImportOptions` is supplied per-call so the use case stays
 * decoupled from the UI's `ImportOption` class. The browser caller
 * passes the existing helper; CLI/tests pass a stub.
 */
export async function loadInitialData<O>(
	deps: SubmitJobsUseCasesDeps,
	input: InitialDataInput<O>,
): Promise<Result<InitialDataResult<O>, LoadInitialDataError>> {
	if (!input.user || !input.user.id) return err("missing_user");
	const importOptions = await input.buildImportOptions(input.user, input.company, input.branches);
	const lastCrops = (await deps.getLastCropOptions?.(input.fresh)) ?? [];
	const nextCrops = (await deps.getNextCropOptions?.(input.fresh)) ?? [];
	return ok({ importOptions, lastCrops, nextCrops });
}

/**
 * Refreshes everything that depends on the active company: branches,
 * regions, growers, deals, and the company's contacts (sorted via
 * `sortContacts`). Five reads happen in parallel except sortContacts
 * which depends on branches; that step is local to the use case so
 * the vm gets a single ready-to-use list.
 */
export async function loadCompanyContext(
	deps: SubmitJobsUseCasesDeps,
	company: Company,
	fresh?: boolean,
): Promise<CompanyContextResult> {
	const [branches, regions, growers, deals, contacts] = await Promise.all([
		deps.getBranchesByCompany?.(company, fresh) ?? Promise.resolve([] as Branch[]),
		deps.getRegionsByCompany?.(company, fresh) ?? Promise.resolve([] as Region[]),
		deps.getGrowersByCompany?.(company, fresh) ?? Promise.resolve([] as Grower[]),
		deps.getDealsByCompany?.(company, fresh) ?? Promise.resolve([] as Deal[]),
		deps.getContactsByCompany?.(company, fresh) ?? Promise.resolve([] as Contact[]),
	]);
	const sorted = [...contacts].sort((a, b) => a.name.localeCompare(b.name));
	return { branches, regions, growers, deals, contacts: sorted };
}

export function makeSubmitJobsUseCases(deps: SubmitJobsUseCasesDeps): SubmitJobsUseCases {
	return {
		submitJobs: (jobs, source) => submitJobs(deps, jobs, source),
		submitJob: (job, source) => submitJob(deps, job, source),
		submitSingleJob: (job, source) => submitSingleJob(deps, job, source),
		loadInitialData: (input) => loadInitialData(deps, input),
		loadCompanyContext: (company, fresh) => loadCompanyContext(deps, company, fresh),
	};
}
